import React, { useState } from "react";
import { Box, Checkbox, IconButton, Menu, MenuItem, useMediaQuery, useTheme } from "@mui/material";
import { ChevronUp, ChevronDown, MoreHorizontal, ChevronLeft, ChevronRight } from "lucide-react";
import { SkeletonRows } from "./Skeletons";

/**
 * DataTable — clean list table.
 *
 * Props:
 *   columns:  [{ key, header, render?(row), width?, align?, sortable?, priority?: "low" }]
 *   rows:     array of row objects
 *   rowKey:   (row) => stable key
 *   onRowClick?: (row) => void
 *   actions?:  (row) => [{ label, onClick, danger? }]   (rendered in a 3-dot menu)
 *   loading:  boolean
 *   empty:    ReactNode   — shown when !loading && rows.length === 0
 *   pagination?: { page, pageSize, total, onChange(nextPage) }
 *   density?: "comfortable" | "compact"
 *   sort?:    { key, direction: "asc"|"desc" }, onSortChange?(next)
 *   selectable?: boolean — when true, prepend a checkbox column for row selection
 *   selectedIds?: Set | array — currently selected row ids
 *   onSelectionChange?: (nextSet: Set) => void
 *   getRowId?: (row) => string — stable id used for selection. Falls back to
 *              `rowKey(row)` and finally to `JSON.stringify(row)`.
 */

function SortIcon({ direction, active }) {
  return (
    <Box sx={{ display: "inline-flex", flexDirection: "column", ml: "4px" }}>
      <ChevronUp
        size={10}
        color={active && direction === "asc" ? "#f97316" : "#cbd5e1"}
        style={{ marginBottom: -2 }}
      />
      <ChevronDown
        size={10}
        color={active && direction === "desc" ? "#f97316" : "#cbd5e1"}
      />
    </Box>
  );
}

function RowActions({ items }) {
  const [anchor, setAnchor] = useState(null);
  const open = Boolean(anchor);
  if (!items || !items.length) return null;
  return (
    <>
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          setAnchor(e.currentTarget);
        }}
        sx={{ color: "#64748b", "&:hover": { backgroundColor: "#f1f5f9" } }}
        aria-label="Row actions"
      >
        <MoreHorizontal size={16} />
      </IconButton>
      <Menu
        anchorEl={anchor}
        open={open}
        onClose={(e) => {
          if (e && e.stopPropagation) e.stopPropagation();
          setAnchor(null);
        }}
        onClick={(e) => e.stopPropagation()}
        PaperProps={{
          sx: {
            borderRadius: "8px",
            border: "1px solid #e2e8f0",
            boxShadow:
              "0 4px 6px -2px rgba(16,24,40,0.05), 0 10px 15px -3px rgba(16,24,40,0.1)",
            minWidth: 160,
          },
        }}
      >
        {items.map((it, i) => (
          <MenuItem
            key={i}
            onClick={(e) => {
              e.stopPropagation();
              it.onClick?.();
              setAnchor(null);
            }}
            sx={{
              fontSize: 14,
              color: it.danger ? "#b91c1c" : "#0f172a",
              "&:hover": { backgroundColor: it.danger ? "#fef2f2" : "#f8fafc" },
            }}
          >
            {it.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

function DataTable({
  columns = [],
  rows = [],
  rowKey = (r) => r.id,
  onRowClick,
  actions,
  loading = false,
  empty,
  pagination,
  density = "comfortable",
  sort,
  onSortChange,
  selectable = false,
  selectedIds,
  onSelectionChange,
  getRowId,
}) {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));

  const visibleColumns = columns.filter((c) => !(isSmall && c.priority === "low"));
  const rowHeight = density === "compact" ? 44 : 56;
  const totalCols =
    visibleColumns.length + (actions ? 1 : 0) + (selectable ? 1 : 0);

  // Normalize selectedIds → Set so callers can pass Set or Array.
  const selectionSet = React.useMemo(() => {
    if (!selectable) return new Set();
    if (selectedIds instanceof Set) return selectedIds;
    if (Array.isArray(selectedIds)) return new Set(selectedIds);
    return new Set();
  }, [selectable, selectedIds]);

  const resolveRowId = React.useCallback(
    (row) => {
      if (getRowId) return getRowId(row);
      try {
        const k = rowKey(row);
        if (k != null) return k;
      } catch (e) {
        // fall through
      }
      return JSON.stringify(row);
    },
    [getRowId, rowKey]
  );

  const visibleRowIds = React.useMemo(
    () => (selectable ? rows.map((r) => resolveRowId(r)) : []),
    [selectable, rows, resolveRowId]
  );

  const allSelected =
    selectable &&
    visibleRowIds.length > 0 &&
    visibleRowIds.every((id) => selectionSet.has(id));
  const someSelected =
    selectable &&
    !allSelected &&
    visibleRowIds.some((id) => selectionSet.has(id));

  const toggleRow = (row, e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    if (!onSelectionChange) return;
    const id = resolveRowId(row);
    const next = new Set(selectionSet);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onSelectionChange(next);
  };

  const toggleAll = () => {
    if (!onSelectionChange) return;
    const next = new Set(selectionSet);
    if (allSelected) {
      visibleRowIds.forEach((id) => next.delete(id));
    } else {
      visibleRowIds.forEach((id) => next.add(id));
    }
    onSelectionChange(next);
  };

  const handleSort = (col) => {
    if (!col.sortable || !onSortChange) return;
    const active = sort?.key === col.key;
    const nextDir = active && sort?.direction === "asc" ? "desc" : "asc";
    onSortChange({ key: col.key, direction: nextDir });
  };

  const renderPagination = () => {
    if (!pagination) return null;
    const { page, pageSize, total, onChange } = pagination;
    const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
    const end = Math.min(page * pageSize, total);
    const lastPage = Math.max(1, Math.ceil(total / pageSize));
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: "16px",
          py: "12px",
          borderTop: "1px solid #e2e8f0",
        }}
      >
        <Box sx={{ fontSize: 13, color: "#64748b" }}>
          Showing {start}–{end} of {total}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <IconButton
            size="small"
            disabled={page <= 1}
            onClick={() => onChange(page - 1)}
            sx={{ color: "#475569", "&:hover": { backgroundColor: "#f1f5f9" } }}
            aria-label="Previous page"
          >
            <ChevronLeft size={16} />
          </IconButton>
          <Box sx={{ fontSize: 13, color: "#475569", px: "8px", minWidth: 60, textAlign: "center" }}>
            Page {page} of {lastPage}
          </Box>
          <IconButton
            size="small"
            disabled={page >= lastPage}
            onClick={() => onChange(page + 1)}
            sx={{ color: "#475569", "&:hover": { backgroundColor: "#f1f5f9" } }}
            aria-label="Next page"
          >
            <ChevronRight size={16} />
          </IconButton>
        </Box>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        borderRadius: "12px",
        border: "1px solid #e2e8f0",
        backgroundColor: "#ffffff",
        boxShadow: "0 1px 2px 0 rgba(16,24,40,0.05)",
        overflow: "hidden",
      }}
    >
      <Box sx={{ overflowX: "auto" }}>
        <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
          <Box component="thead">
            <Box component="tr" sx={{ height: 40, backgroundColor: "#f8fafc" }}>
              {selectable && (
                <Box
                  component="th"
                  sx={{
                    width: 44,
                    px: "12px",
                    py: "10px",
                    borderBottom: "1px solid #e2e8f0",
                    textAlign: "left",
                  }}
                >
                  <Checkbox
                    size="small"
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={toggleAll}
                    inputProps={{ "aria-label": "Select all rows" }}
                    sx={{
                      p: 0,
                      color: "#cbd5e1",
                      "&.Mui-checked": { color: "#f97316" },
                      "&.MuiCheckbox-indeterminate": { color: "#f97316" },
                    }}
                  />
                </Box>
              )}
              {visibleColumns.map((col) => {
                const active = sort?.key === col.key;
                return (
                  <Box
                    key={col.key}
                    component="th"
                    onClick={() => handleSort(col)}
                    sx={{
                      textAlign: col.align || "left",
                      fontSize: 12,
                      fontWeight: 600,
                      color: "#64748b",
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                      px: "12px",
                      py: "10px",
                      borderBottom: "1px solid #e2e8f0",
                      cursor: col.sortable ? "pointer" : "default",
                      width: col.width,
                      whiteSpace: "nowrap",
                      userSelect: "none",
                    }}
                  >
                    <Box sx={{ display: "inline-flex", alignItems: "center" }}>
                      {col.header}
                      {col.sortable && (
                        <SortIcon direction={sort?.direction} active={active} />
                      )}
                    </Box>
                  </Box>
                );
              })}
              {actions && (
                <Box
                  component="th"
                  sx={{
                    width: 48,
                    px: "12px",
                    py: "10px",
                    borderBottom: "1px solid #e2e8f0",
                  }}
                />
              )}
            </Box>
          </Box>
          <Box component="tbody">
            {loading ? (
              <Box component="tr">
                <Box component="td" colSpan={totalCols} sx={{ p: 0 }}>
                  <SkeletonRows rows={5} columns={totalCols} />
                </Box>
              </Box>
            ) : rows.length === 0 ? (
              <Box component="tr">
                <Box
                  component="td"
                  colSpan={totalCols}
                  sx={{ p: 0, textAlign: "center" }}
                >
                  {empty || (
                    <Box sx={{ py: "48px", color: "#94a3b8", fontSize: 14 }}>
                      No data
                    </Box>
                  )}
                </Box>
              </Box>
            ) : (
              rows.map((row) => {
                const rowActions = actions ? actions(row) : null;
                const rid = selectable ? resolveRowId(row) : null;
                const isSelected = selectable && selectionSet.has(rid);
                return (
                  <Box
                    component="tr"
                    key={rowKey(row)}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    sx={{
                      height: rowHeight,
                      cursor: onRowClick ? "pointer" : "default",
                      transition: "background-color 150ms ease-out",
                      backgroundColor: isSelected ? "#fff7ed" : undefined,
                      "&:hover": {
                        backgroundColor: isSelected ? "#ffedd5" : "#f8fafc",
                      },
                      "&:last-child td": { borderBottom: "none" },
                    }}
                  >
                    {selectable && (
                      <Box
                        component="td"
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          width: 44,
                          px: "12px",
                          py: "10px",
                          borderBottom: "1px solid #e2e8f0",
                          verticalAlign: "middle",
                        }}
                      >
                        <Checkbox
                          size="small"
                          checked={isSelected}
                          onChange={(e) => toggleRow(row, e)}
                          onClick={(e) => e.stopPropagation()}
                          inputProps={{ "aria-label": "Select row" }}
                          sx={{
                            p: 0,
                            color: "#cbd5e1",
                            "&.Mui-checked": { color: "#f97316" },
                          }}
                        />
                      </Box>
                    )}
                    {visibleColumns.map((col) => (
                      <Box
                        key={col.key}
                        component="td"
                        sx={{
                          textAlign: col.align || "left",
                          fontSize: 14,
                          color: "#334155",
                          px: "12px",
                          py: "10px",
                          borderBottom: "1px solid #e2e8f0",
                          verticalAlign: "middle",
                        }}
                      >
                        {col.render ? col.render(row) : row[col.key]}
                      </Box>
                    ))}
                    {actions && (
                      <Box
                        component="td"
                        sx={{
                          textAlign: "right",
                          px: "8px",
                          py: "6px",
                          borderBottom: "1px solid #e2e8f0",
                        }}
                      >
                        <RowActions items={rowActions} />
                      </Box>
                    )}
                  </Box>
                );
              })
            )}
          </Box>
        </Box>
      </Box>
      {renderPagination()}
    </Box>
  );
}

export default DataTable;
